/**
 * SPEC-08 §6 — FX-rate paste sanitizer.
 *
 * Real-world clipboards from auditors include junk: the user copies a
 * cell from an Excel column and ends up with stray trailing characters
 * (`50.2859s"`, `30,4567 TRY`, `'30.45'`). Validation rejects that
 * value, the auditor sees a Zod error and re-types it manually. A small
 * paste-time scrub removes the friction without ever altering an
 * already-valid number.
 *
 * Policy:
 *   • Trim leading/trailing whitespace.
 *   • Strip wrapping single/double quotes.
 *   • Drop any non-digit / non-separator suffix (e.g. `30.45 TRY` →
 *     `30.45`; `50.2859s"` → `50.2859`).
 *   • Normalise separators to a JS-parseable `.` decimal, using the
 *     active locale to resolve the single genuinely-ambiguous case (a
 *     lone separator with a 3-digit tail — see below).
 *   • If the cleaned string doesn't look like a number at all, return
 *     the ORIGINAL text so RHF + Zod can surface the validation
 *     message as before — never silently swallow user input.
 *
 * Finding F1 — the previous "the last separator is always the decimal"
 * rule silently divided whole-thousand values by 1000: a paste of
 * `3.000` (three thousand, TR grouping) or `3,000` (three thousand, EN
 * grouping) became `3`, with no validation error. The fix below keeps the
 * robust last-separator rule for the multi-separator case (so cross-locale
 * pastes like `1.234,56` and `1,234.56` both work), and resolves the only
 * ambiguous case — a SINGLE separator with exactly 3 trailing digits — by
 * the active locale's decimal convention (tr-TR decimal `,`; en-US decimal
 * `.`). A tail that is not 3 digits long is unambiguously a decimal, since a
 * grouping separator always groups exactly 3 digits.
 *
 * Pure function, no React deps (locale is injected). Easy to unit test.
 */

const NUMERIC_PREFIX_RE = /^[+-]?[\d.,\s']+/;

export function sanitizeFxRatePaste(input: string, locale = "en-US"): string {
  if (typeof input !== "string") return String(input ?? "");
  let s = input.trim();
  // Strip wrapping single/double quotes (Excel sometimes prefixes a
  // text-mode cell with a quote).
  if (
    (s.startsWith('"') && s.endsWith('"')) ||
    (s.startsWith("'") && s.endsWith("'"))
  ) {
    s = s.slice(1, -1).trim();
  }
  // Keep only the numeric prefix (drops trailing letters / currency
  // codes / stray punctuation like the `s"` in the screenshot).
  const m = s.match(NUMERIC_PREFIX_RE);
  if (!m) return input;
  let core = m[0].trim();

  const localeDecimalSep = locale === "tr-TR" ? "," : ".";
  const commaCount = (core.match(/,/g) ?? []).length;
  const dotCount = (core.match(/\./g) ?? []).length;
  const sepCount = commaCount + dotCount;

  if (sepCount === 0) {
    core = core.replace(/[\s']/g, "");
  } else if (sepCount === 1) {
    const sepIndex = Math.max(core.lastIndexOf(","), core.lastIndexOf("."));
    const sep = core[sepIndex];
    const head = core.slice(0, sepIndex).replace(/[\s']/g, "");
    const tail = core.slice(sepIndex + 1).replace(/[\s']/g, "");
    // A grouping separator always has exactly 3 trailing digits, so a tail
    // that is not 3 digits long is unambiguously a decimal. When it IS 3
    // digits the value is ambiguous (`3.000` = 3000 grouped, or 3.0 decimal);
    // resolve it by the active locale's decimal separator.
    //
    // D5 — KNOWN, ACCEPTED cross-locale ambiguity (locked by tests below).
    // A single-separator value with a 3-digit tail (`1.500`, `30.500`) is
    // GENUINELY ambiguous and is resolved purely by the active locale. A
    // value typed in the OTHER locale and pasted here can therefore be
    // mis-scaled 1000× (en-US `1.500` = 1.5 read as TR `1500`, or vice
    // versa) with NO validation error, because both readings are valid
    // numbers. This is intentional and deliberately NOT "guessed" away:
    // the function's contract is `string -> string` (cleaned number, or
    // the original text so RHF + Zod surface an error). There is no
    // out-of-band channel to flag "ambiguous", and both callers
    // (EngagementClosingFxRatesSection paste handler, parseExcelPaste)
    // consume the bare string — adding an ambiguity signal would change
    // the return type and ripple through every call site. We keep the
    // locale-resolved behavior, pin it with tests, and rely on the
    // auditor's TCMB-`Manuel` badge + visible cell value to catch a
    // mis-scale. Revisit only if the caller contract is widened.
    // Finding TR1 — a grouped thousand always has a non-empty, non-zero,
    // 1–3 digit head (`3.000`, `32.000`). A head of "0" / "" / 4+ digits
    // can therefore only be a decimal: without this guard a sub-1 FX
    // cross-rate like `0.011` was read as TR-grouped `0011` (= 11), a
    // 1000× mis-scale with no validation error.
    const headDigits = head.replace(/^[+-]/, "");
    const headCanGroup =
      headDigits.length >= 1 &&
      headDigits.length <= 3 &&
      !/^0+$/.test(headDigits);
    const isDecimal =
      tail.length !== 3 || sep === localeDecimalSep || !headCanGroup;
    core = isDecimal ? `${head}.${tail}` : `${head}${tail}`;
  } else {
    // Multiple separators → grouped number; the LAST separator is the
    // decimal. Robust across both `1.234,56` and `1,234.56` regardless of
    // locale (earlier separators are grouping and are stripped).
    const lastSep = Math.max(core.lastIndexOf(","), core.lastIndexOf("."));
    const head = core.slice(0, lastSep).replace(/[\s.,']/g, "");
    const tail = core.slice(lastSep + 1).replace(/[\s.,']/g, "");
    core = `${head}.${tail}`;
  }
  // Final sanity: did we end up with a parseable number? If not, fall
  // back to the raw input so the form's own validation surfaces a
  // meaningful error to the user.
  if (!Number.isFinite(Number(core))) return input;
  return core;
}

/**
 * Finding TR1 — boundary normalizer for free-text decimal form inputs
 * (`<Input type="text" inputMode="decimal">`). Those fields fed raw
 * locale strings into `Number()`-based coercion, so TR grouping
 * `10.000` (= ten thousand) was silently accepted as 10 — a 1000×
 * understatement with no validation error.
 *
 * Unlike the paste sanitizer above, this NEVER strips trailing junk:
 * anything that isn't purely numeric (sign / digits / separators /
 * spaces) is returned verbatim so strict `Number()` coercion still
 * rejects it ("1.5x" must stay an error — see lib/format Finding F6).
 * Purely numeric strings route through `sanitizeFxRatePaste` so both
 * TR grouping and comma decimals (`34,50`) coerce to the intended
 * magnitude. Pure function — callers inject the locale (typically
 * `getCurrentLocale()` from `@/lib/format`).
 */
export function normalizeDecimalInput(
  input: string,
  locale = "en-US",
): string {
  const trimmed = input.trim();
  if (!/^[+-]?[\d\s.,']+$/.test(trimmed)) return input;
  return sanitizeFxRatePaste(trimmed, locale);
}
